import React from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Doughnut, Pie, Bar } from 'react-chartjs-2';
import { useTenders } from '../../hooks/TenderContext';
import { getWinRateOverTime, getFieldDistribution, getStatusDistribution, getMonthlyCount } from '../../utils/helpers';
import { motion } from 'framer-motion';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const CHART_COLORS = [
  '#1565C0', '#00897B', '#FF6F00', '#C62828', '#F9A825',
  '#7B1FA2', '#00838F', '#558B2F', '#D84315', '#4527A0',
];

const ChartsSection = () => {
  const theme = useTheme();
  const { tenders } = useTenders();
  const isDark = theme.palette.mode === 'dark';

  const gridColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const textColor = theme.palette.text.secondary;

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: textColor, font: { family: 'Rubik', size: 12 } },
        position: 'bottom',
      },
      tooltip: {
        rtl: true,
        textDirection: 'rtl',
        titleFont: { family: 'Rubik' },
        bodyFont: { family: 'Rubik' },
        backgroundColor: isDark ? '#333' : '#fff',
        titleColor: isDark ? '#fff' : '#333',
        bodyColor: isDark ? '#ccc' : '#666',
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
    },
  };

  // 1. Win Rate Over Time (Line Chart)
  const winRateData = getWinRateOverTime(tenders);
  const lineData = {
    labels: winRateData.labels,
    datasets: [{
      label: 'אחוזי זכייה',
      data: winRateData.data,
      borderColor: '#1565C0',
      backgroundColor: 'rgba(21, 101, 192, 0.1)',
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
    }],
  };

  // 2. Field Distribution (Doughnut)
  const fieldData = getFieldDistribution(tenders);
  const doughnutData = {
    labels: fieldData.labels,
    datasets: [{
      data: fieldData.data,
      backgroundColor: CHART_COLORS.slice(0, fieldData.labels.length),
      borderWidth: 2,
      borderColor: theme.palette.background.paper,
    }],
  };

  // 3. Status Distribution (Pie)
  const statusData = getStatusDistribution(tenders);
  const pieData = {
    labels: statusData.labels,
    datasets: [{
      data: statusData.data,
      backgroundColor: ['#1565C0', '#FF6F00', '#2E7D32'],
      borderWidth: 2,
      borderColor: theme.palette.background.paper,
    }],
  };

  // 4. Monthly Count (Bar)
  const monthlyData = getMonthlyCount(tenders);
  const barData = {
    labels: monthlyData.labels,
    datasets: [{
      label: 'מכרזים',
      data: monthlyData.data,
      backgroundColor: '#1565C0',
      borderRadius: 6,
      borderSkipped: false,
    }],
  };

  const charts = [
    {
      title: 'אחוזי זכייה לאורך זמן',
      component: (
        <Line data={lineData} options={{
          ...commonOptions,
          scales: {
            x: { grid: { color: gridColor }, ticks: { color: textColor, font: { family: 'Rubik', size: 11 } } },
            y: {
              grid: { color: gridColor },
              ticks: { color: textColor, callback: v => v + '%', font: { family: 'Rubik', size: 11 } },
              min: 0, max: 100,
            },
          },
        }} />
      ),
    },
    {
      title: 'פילוח לפי תחום',
      component: <Doughnut data={doughnutData} options={{ ...commonOptions, cutout: '60%' }} />,
    },
    {
      title: 'פילוח לפי סטטוס',
      component: <Pie data={pieData} options={commonOptions} />,
    },
    {
      title: 'מכרזים לפי חודש',
      component: (
        <Bar data={barData} options={{
          ...commonOptions,
          scales: {
            x: { grid: { display: false }, ticks: { color: textColor, font: { family: 'Rubik', size: 11 } } },
            y: { grid: { color: gridColor }, ticks: { color: textColor, stepSize: 1, font: { family: 'Rubik', size: 11 } } },
          },
        }} />
      ),
    },
  ];

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
      gap: 2,
      mb: 3,
    }}>
      {charts.map((chart, index) => (
        <motion.div
          key={chart.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 + index * 0.1, duration: 0.3 }}
        >
          <Card sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {chart.title}
              </Typography>
              <Box sx={{ height: 250, position: 'relative' }}>
                {chart.component}
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </Box>
  );
};

export default ChartsSection;
